import { useCallback, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { Button } from "@/components/ui";
import { resolveAssetUrl } from "@/components/ui";
import type { FaceArea, FaceAreaPoint } from "@/lib/types/config";
import { cn } from "@/lib/utils/cn";

type Mode = "rectangle" | "lasso";

/**
 * Defines where the camera feed sits inside a frame overlay.
 *
 * Everything is stored as fractions (0–1) of the frame, never pixels: the same
 * config drives an on-screen preview at a few hundred pixels and a 1024px print
 * composite server-side, and pixel values would only be correct for one of them.
 */
export function FaceAreaEditor({
  overlayUrl, value, onChange,
}: {
  overlayUrl?: string;
  value?: FaceArea;
  onChange: (area: FaceArea) => void;
}) {
  const [mode, setMode] = useState<Mode>(value?.points?.length ? "lasso" : "rectangle");
  const [drawing, setDrawing] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<FaceAreaPoint | null>(null);
  const dragMode = useRef<"draw" | "move" | null>(null);
  const moveOrigin = useRef<{ pointer: FaceAreaPoint; area: FaceArea } | null>(null);

  const resolved = resolveAssetUrl(overlayUrl);

  const toFraction = useCallback((event: PointerEvent): FaceAreaPoint => {
    const rect = surfaceRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  }, []);

  function onPointerDown(event: PointerEvent) {
    if (!resolved) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toFraction(event);

    if (mode === "lasso") {
      // Lasso builds a polygon click by click; a double-click closes it.
      onChange({ points: [...(value?.points ?? []), point] });
      return;
    }

    const inside =
      value?.width !== undefined &&
      point.x >= (value.x ?? 0) && point.x <= (value.x ?? 0) + (value.width ?? 0) &&
      point.y >= (value.y ?? 0) && point.y <= (value.y ?? 0) + (value.height ?? 0);

    if (inside) {
      dragMode.current = "move";
      moveOrigin.current = { pointer: point, area: { ...value! } };
    } else {
      dragMode.current = "draw";
      dragStart.current = point;
      setDrawing(true);
      onChange({ x: point.x, y: point.y, width: 0, height: 0 });
    }
  }

  function onPointerMove(event: PointerEvent) {
    if (!dragMode.current || mode === "lasso") return;
    const point = toFraction(event);

    if (dragMode.current === "move" && moveOrigin.current) {
      const { pointer, area } = moveOrigin.current;
      const width = area.width ?? 0;
      const height = area.height ?? 0;
      onChange({
        ...area,
        x: clamp((area.x ?? 0) + (point.x - pointer.x), 0, 1 - width),
        y: clamp((area.y ?? 0) + (point.y - pointer.y), 0, 1 - height),
      });
      return;
    }

    const start = dragStart.current;
    if (!start) return;
    onChange({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y),
    });
  }

  function onPointerUp() {
    dragMode.current = null;
    dragStart.current = null;
    moveOrigin.current = null;
    setDrawing(false);
  }

  const points = value?.points ?? [];
  const hasRect = value?.width !== undefined && (value.width ?? 0) > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg bg-ink-800 p-1">
          {(["rectangle", "lasso"] as Mode[]).map((option) => (
            <button
              key={option}
              onClick={() => { setMode(option); onChange(option === "lasso" ? { points: [] } : {}); }}
              className={cn(
                "min-h-10 rounded-lg px-3 text-xs font-medium capitalize transition-colors",
                mode === option ? "bg-accent font-semibold text-accent-ink" : "text-haze-300 hover:bg-ink-700 hover:text-haze-100",
              )}
            >
              {option}
            </button>
          ))}
        </div>

        {mode === "lasso" && points.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => onChange({ points: points.slice(0, -1) })}>
            Undo point
          </Button>
        )}
        {(hasRect || points.length > 0) && (
          <Button size="sm" variant="ghost" onClick={() => onChange(mode === "lasso" ? { points: [] } : {})}>
            Clear
          </Button>
        )}
        <span className="ml-auto text-xs text-haze-400">
          {mode === "rectangle" ? "Drag to draw. Drag inside to reposition." : "Click to add points."}
        </span>
      </div>

      <div
        ref={surfaceRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={cn(
          "relative aspect-[3/4] w-full max-w-sm select-none overflow-hidden rounded-lg border border-ink-600 bg-ink-900",
          resolved ? "cursor-crosshair" : "cursor-not-allowed",
        )}
        style={{
          backgroundImage:
            "repeating-conic-gradient(#12161b 0% 25%, #0d1116 0% 50%)",
          backgroundSize: "16px 16px",
        }}
      >
        {resolved ? (
          <img src={resolved} alt="" draggable={false} className="pointer-events-none h-full w-full object-contain" />
        ) : (
          <div className="grid h-full place-items-center px-6 text-center text-xs text-haze-400">
            Upload a frame image first, then define the face cutout area.
          </div>
        )}

        {hasRect && (
          <div
            className={cn(
              "pointer-events-none absolute border-2 border-accent bg-accent/20",
              drawing && "border-dashed",
            )}
            style={{
              left: `${(value!.x ?? 0) * 100}%`,
              top: `${(value!.y ?? 0) * 100}%`,
              width: `${(value!.width ?? 0) * 100}%`,
              height: `${(value!.height ?? 0) * 100}%`,
            }}
          />
        )}

        {points.length > 0 && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon
              points={points.map((p) => `${p.x * 100},${p.y * 100}`).join(" ")}
              fill="rgba(212,241,79,0.2)"
              stroke="#d4f14f"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
        {points.map((point, index) => (
          <span
            key={index}
            className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
          />
        ))}
      </div>

      <p className="text-xs leading-relaxed text-haze-400">
        The face area is where the camera feed appears on the frame. Values are fractional (0–1) of the
        frame's dimensions, and the AI renders at exactly this region's pixel size.
      </p>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
